import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { IconButton, Theme, Tooltip } from '@mui/material';
import { green, red } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import React, { ReactChild, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useRecoilStateLoadable, useSetRecoilState } from 'recoil';

import { IUserAtom, Role, user } from '../../../../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../../../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../../../services/rest/makeAPIRequest';
import { IVwQueueMembers } from '../../../../../services/rest/makeApiRequest.t';
import { ModalWithStyles } from '../../../../components/modals/ModalWithStyles';
import { DataTable, IColDef } from '../queues-table/DataTable';

const useStyles = makeStyles((theme: Theme) => ({
  '& .MuiTable-root': {
    tableLayout: 'fixed',
  },
  centerColumn: {
    textAlign: 'center',
  },
  teamLink: {
    paddingInlineStart: 0,
    marginBottom: 0,
    listStyleType: 'none',
  },
  iconRotate: {
    // reload icon animation
    transform: 'rotate(-8100deg)',
    transition: 'all 15s linear',
  },
  fullWidth: {
    /* override DataGrid element style (NOTE: default 100px) */
    width: '100% !important',
    maxWidth: 'none !important',
    minWidth: '150px !important',
  },
}));

const radioStyles = makeStyles({
  centerColumn: (props: { justify: string }) => ({
    display: 'flex',
    flexGrow: 1,
    justifyContent: props.justify ?? '',
  }),
  green: {
    color: green[500],
  },
  red: {
    color: red[500],
  },
});

const RadioColumn = (props: {
  on: boolean;
  color: 'green' | 'red';
  /** tooltip for icon */
  tooltip?: string | boolean | ReactChild;
  /** default: center */
  align?: 'center' | 'left' | 'right';
}) => {
  const classes = radioStyles({ justify: props.align ?? 'center' });

  const content = useMemo(
    () =>
      props.on ? (
        <RadioButtonCheckedIcon
          className={props.color === 'green' ? classes.green : classes.red}
        />
      ) : (
        <RadioButtonUncheckedIcon />
      ),
    [props.on, props.color, classes]
  );

  return (
    <div className={classes.centerColumn}>
      {props.tooltip !== undefined ? (
        <Tooltip title={props.tooltip}>
          <IconButton size="large">{content}</IconButton>
        </Tooltip>
      ) : (
        content
      )}
    </div>
  );
};
const AgentAvailableTooltip = (props: {
  value: boolean;
  displayText: string;
}) => {
  const { value, displayText } = props;
  return (
    <div>
      {value ? (
        <CheckIcon style={{ color: green[500], fontSize: 15 }} />
      ) : (
        <ClearIcon style={{ color: red[500], fontSize: 15 }} />
      )}
      <span>{displayText}</span>
    </div>
  );
};
// A custom hook that builds on useLocation to parse
// the query string for you.
export function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}

/**
 * Queue editing component
 *
 * @param {Object} props
 * @param {string} [props.id] Queue id
 * @param {boolean} props.show True if modal shown
 * @param {onHideCallback} props.onHide Function to hide modal
 *
 * @see https://github.com/microsoft/vscode/issues/41296
 */
export function QueueMembersDialog(props: {
  id?: string;
  show: boolean;
  onHide: () => void;
  setUpdatedRowId?: (rowId: string | undefined) => void;
}) {
  const classes = useStyles();
  const queryParams = useQueryParams();
  const queueName = queryParams.get('queuename');

  const [userAtomLoadable, _setUserAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, unknown>
  >(user);

  const userAtomContents = userAtomLoadable?.contents as IUserAtom;

  const setRefetchToggleQueueMembers = useSetRecoilState(
    refetchToggle(endpointEnum.GET_QUEUE_MEMBERS)
  );

  type TTeams = {
    teamId: number;
    teamName: string;
  };

  const [queueMembersCached, queueMembersLoadable] = makeApiRequestCached<
    (IVwQueueMembers & {
      id: number;
      teams: TTeams[];
    })[]
  >(
    {
      endpoint: endpointEnum.GET_QUEUE_MEMBERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_QUEUE_MEMBERS,
        queueid: props.id,
      }),
    },
    (loadable, cached) =>
      Array.isArray(loadable.contents?.response?.data)
        ? loadable.contents.response.data
            .map((row: IVwQueueMembers) => ({
              ...row,
              id: row.userid,
              teams: JSON.parse(row.teams),
            }))
            .sort((l, r) =>
              l?.agentName
                ?.toLowerCase()
                .localeCompare(r?.agentName?.toLowerCase())
            )
        : cached.current,
    [] // initialize cache
  );

  /**
   * Create column for DataGrid renderer
   * use useMemo to prevent unneeded rerenderings
   */
  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: 'id',
        headerName: 'UserID',
        width: 75,
      },
      {
        field: 'teams',
        headerName: 'Teams',
        width: 150,
        renderCell: (params) =>
          React.useMemo(
            () => (
              <article>
                <ul className={classes.teamLink}>
                  {(params.value as TTeams[])?.map((team) => (
                    <li key={team.teamId}>
                      <NavLink
                        className="menu-link"
                        to={`/manage/teams/${team.teamId}/edit`}
                      >
                        {team.teamName}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </article>
            ),
            [params.value, classes]
          ),
      },
      {
        field: 'agentName',
        headerName: 'Name',
        width: 150,
        renderCell: (params) => (
          <NavLink to={`/manage/agents/${params.row.userid}/edit`}>
            {params.value}
          </NavLink>
        ),
      },
      {
        field: 'subscribed',
        headerName: 'Subscribed',
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => (
          <RadioColumn
            on={Boolean(params.value)}
            color="green"
            tooltip="Green when subscribed to queue"
          />
        ),
        width: 100,
      },
      {
        field: 'DND',
        headerName: 'DND',
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => (
          <RadioColumn
            on={Boolean(params.value)}
            color="red"
            tooltip="Red when DND is on"
          />
        ),
        width: 100,
      },
      {
        field: 'onCall',
        headerName: 'On Call',
        headerAlign: 'center',
        align: 'center',
        cellClassName: classes.centerColumn,
        renderCell: (params) => (
          <RadioColumn
            on={Boolean(params.value)}
            color="red"
            tooltip="Red when on call"
          />
        ),
        width: 100,
      },
      {
        field: 'canReceiveCalls',
        headerName: 'Can Receive Calls',
        headerClassName: classes.centerColumn,
        renderCell: (params) => (
          <RadioColumn
            on={Boolean(params.value)}
            color="green"
            tooltip="Green when subscribed, not on a call and DND is off"
          />
        ),
        width: 100,
      },
      ...((userAtomContents?.user?.role === Role.SUPERADMIN
        ? [
            {
              field: 'agentAvailable',
              headerName: 'Agent Available',
              headerClassName:
                classes.centerColumn + ' superStyle table-col-xs',
              width: 100,
              renderCell: (params) => (
                <RadioColumn
                  on={Boolean(params.value)}
                  color="green"
                  tooltip={
                    <div>
                      <div>
                        Green when the agent can receive calls and the following
                        are all true:
                      </div>
                      <AgentAvailableTooltip
                        value={Number(params.row.subscribed) === 1}
                        displayText={' Subscribed to Queue'}
                      />
                      <AgentAvailableTooltip
                        value={Number(params.row.businessActive) === 1}
                        displayText={' Associated with Team'}
                      />
                      <AgentAvailableTooltip
                        value={Number(params.row.powercallActive) === 1}
                        displayText={' Powercall is Open'}
                      />
                      <AgentAvailableTooltip
                        value={Number(params.row.userActive) === 1}
                        displayText={' Status is Active'}
                      />
                      <AgentAvailableTooltip
                        value={Number(params.row.agentReserved ? 0 : 1) === 1}
                        displayText={' Not Reserved'}
                      />
                    </div>
                  }
                />
              ),
            },
          ]
        : []) as IColDef[]),
    ],
    [
      queueMembersCached.current,
      queueMembersLoadable.state,
      userAtomContents?.user?.role,
    ]
  );

  const [updateTime, setUpdateTime] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setUpdateTime((state) => {
        // update every 60 seconds
        if (state >= 60) {
          setRefetchToggleQueueMembers({});
          return 0;
        }
        return state + 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  return (
    <ModalWithStyles
      title={queueName || 'Queue Members'}
      show={props.show}
      onHide={props.onHide}
      onSubmit={() => {
        setRefetchToggleQueueMembers({});
        setUpdateTime(0);
      }}
      infoLabel={'updated: ' + updateTime + ' seconds ago'}
      noPaddingDialog={true}
      submitLabel="Refresh"
    >
      <DataTable
        style={{ tableLayout: 'fixed' }}
        rows={queueMembersCached?.current ?? []}
        columns={columns}
        loading={queueMembersLoadable.state === 'loading'}
        tableLayoutFixed={true}
      />
    </ModalWithStyles>
  );
}
